import { router } from "@inertiajs/react"
import { useEffect, useRef } from "react"
import { getCableConsumer, subscribeCableChannel } from "@/lib/cable-consumer"
import type { SpendLimitShare } from "@/types/billing"

const RELOAD_THROTTLE_MS = 6_000

function reloadSpendLimit() {
  router.reload({ only: ["spend_limit"], preserveUrl: true, preserveErrors: true, except: ["flash"] })
}

/** Канал личного лимита: сигнал перечитывает проп (docs/billing_limits.md §11). */
export function useSpendLimit(shared: SpendLimitShare | null, companyId: number | null): SpendLimitShare | null {
  const enabled = shared != null && companyId != null
  const reconnectRef = useRef(false)
  const timerRef = useRef<number | null>(null)

  useEffect(() => {
    if (!enabled || companyId == null) return

    const scheduleReload = () => {
      if (timerRef.current !== null) return
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null
        reloadSpendLimit()
      }, RELOAD_THROTTLE_MS)
    }

    const unsubscribe = subscribeCableChannel(
      getCableConsumer(),
      { channel: "Companies::SpendLimitChannel", company_id: companyId },
      {
        connected() {
          if (!reconnectRef.current) return
          reconnectRef.current = false
          scheduleReload()
        },
        disconnected() {
          reconnectRef.current = true
        },
        received(data: unknown) {
          if (!data || typeof data !== "object") return
          if ((data as { type?: string }).type !== "spend_limit_changed") return
          scheduleReload()
        },
      },
    )

    return () => {
      unsubscribe()
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current)
        timerRef.current = null
      }
    }
  }, [companyId, enabled])

  return shared
}
